import React, { useState, useCallback, useEffect } from 'react';

import { humanizeDateTime } from '@common/utils/date';
import {
  isResponseValidForRcb, requestCallback,
} from '../utils/RequestCallback';
import { SLUG_TO_CONFIG } from '../config';
import { trackGAEvent } from '@common/utils/ga';

import ChatbotContext from '~chatbot/context/ChatbotContext';
import promptResponse from '~chatbot/api/promptResponse';
import useChatbot from '~chatbot/hooks/useChatbot';
import { handleTracking } from '~chatbot/utils/analytics';

function ChatbotProvider({ children, chatbotSlug }) {
  const [openWidget, setisOpenWidget] = useState(false);
  const [chatbotAccess, setChatbotAccess] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [chats, setChats] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const [chatHistory, setChatHistory] = useState(false);
  const config = SLUG_TO_CONFIG[chatbotSlug];

  /* Gets Access to Chatbot before getting access to sockets and prompt
  API calls */
  useEffect(() => {
    const fetchAccess = async () => {
      const response = await promptResponse.chatbotAccess(chatbotSlug);
      setChatbotAccess(response);
    };
    fetchAccess();
  }, [chatbotAccess, chatbotSlug]);

  const socket = useChatbot((chatbotAccess ? 'ChatbotStreamChannel' : null), {
    slug: chatbotSlug,
  });

  const askQuestion = useCallback(() => {
    setChats((prevChats) => ({
      ...prevChats,
      [`user_query_${Object.keys(prevChats || {})}`]:
        { owner: 'user', message: prompt },
    }));
    setIsFetching(true);
    handleTracking({
      click_type: 'response_start_fetching',
      custom: {
        current_time: new Date(),
      },
    });
  }, [prompt]);

  const insertChat = useCallback((data, sender) => {
    if (sender === 'user') {
      setIsFetching(false);
      setChats((prevChats) => ({
        ...prevChats,
        [`system_query_${data.id}`]: {
          owner: 'system', packets: [], response: '', time: data.timestamp,
        },
      }));
      handleTracking({
        click_type: 'response_fetched',
        click_text: 'user',
        custom: {
          current_time: new Date(),
        },
      });
    } else if (sender === 'system') {
      if (isFetching) {
        handleTracking({
          click_type: 'response_fetched',
          click_text: 'system',
          custom: {
            current_time: new Date(),
          },
        });
        setIsFetching(false);
      }
      setChats((prevChats) => ({
        ...prevChats,
        [`system_query_${data.id}`]: {
          ...prevChats[`system_query_${data.id}`],
          owner: prevChats[`system_query_${data.id}`]?.owner || 'system',
          packets: [
            ...prevChats[`system_query_${data.id}`]?.packets || [],
            { packet: data.packet_id, words: data.message },
          ].sort((a, b) => a.packet - b.packet),
        },
      }));
      setChatHistory(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateResponse = useCallback((data) => {
    setChats((prevChats) => ({
      ...prevChats,
      [`system_query_${data.id}`]: {
        ...prevChats[`system_query_${data.id}`],
        response: data.message,
      },
    }));
    setChatHistory(false);
  }, []);

  /* Setup socket listeners intially once channel gets subscribed */
  useEffect(() => {
    const handleChatOpen = (res) => insertChat(res.data, 'user');
    const handleChatEnd = (res) => updateResponse(res.data);
    const handleChatError = () => setChatbotAccess(false);
    const handleChatPacket = (res) => insertChat(res.data, 'system');

    if (socket) {
      socket.on('_chatbot_stream_open', handleChatOpen);
      socket.on('_chatbot_stream_packet', handleChatPacket);
      socket.on('_chatbot_stream_end', handleChatEnd);
      socket.on('_chatbot_stream_error', handleChatError);

      return () => {
        socket.off('_chatbot_stream_open', handleChatOpen);
        socket.off('_chatbot_stream_packet', handleChatPacket);
        socket.off('_chatbot_stream_end', handleChatEnd);
        socket.off('_chatbot_stream_error', handleChatError);
      };
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  /* Debounces the chat history API call everytime for 5 seconds whenever
  the chat state updates which happens after every packet is received until
  stream is ended. This is done to make sure if the stream end event is missed
  it fetches the full response from the chat history API as a backup
  */
  useEffect(() => {
    const getFullResponse = setTimeout(async () => {
      if (chatHistory) {
        const data = await promptResponse.getChats(chatbotSlug, 0, 1);
        updateResponse({
          id: data.messages[0].assisted_request_id,
          message: data.messages[0].content,
        });
      }
    }, 500);

    return () => clearTimeout(getFullResponse);
  }, [chatHistory, chatbotSlug, chats, updateResponse]);

  const handleSubmit = useCallback(async () => {
    try {
      if (isResponseValidForRcb(prompt, chats)) {
        setisOpenWidget(false);
        requestCallback();
        setPrompt('');
        trackGAEvent({
          category: 'scaler_bot_rcb',
          action: `valid_response`,
        });
      } else if (prompt !== '') {
        askQuestion();
        await promptResponse.sendQuestion(chatbotSlug, prompt);
        setPrompt('');
      }
    } catch (error) {
      setChatbotAccess(false);
    }
  }, [askQuestion, chatbotSlug, chats, prompt]);

  const handleAction = useCallback(() => {
    if (openWidget && chatbotAccess) {
      handleSubmit();
    } else {
      setisOpenWidget(true);
    }
  }, [chatbotAccess, handleSubmit, openWidget, setisOpenWidget]);

  const getTimings = (
    currentDate = new Date(),
  ) => humanizeDateTime(currentDate);

  return (
    <ChatbotContext.Provider
      value={{
        openWidget,
        setisOpenWidget,
        prompt,
        setPrompt,
        chatbotAccess,
        setChatbotAccess,
        handleAction,
        handleSubmit,
        chatbotSlug,
        chats,
        setChats,
        isFetching,
        setIsFetching,
        getTimings,
        config,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
}

export default ChatbotProvider;
